import { useEffect, useRef, useState } from 'react';
import type { Unsubscribe } from 'firebase/firestore';

import { env } from './env.js';
import { logger } from './logger.js';

/**
 * Mirrors `apps/dashboard/src/lib/use-tracked-snapshot.ts`. Feature code
 * never calls `onSnapshot` directly — it consumes a `Subscribe<T>`
 * returned by a repo so the ESLint `no-restricted-imports` rule can
 * keep `firebase/firestore` confined to `lib/repositories/`.
 *
 * Dev-mode counter logs every subscribe/unsubscribe so leaks surface as
 * a monotonically-growing live count in the browser console.
 */
export type Subscribe<T> = (cb: (value: T) => void) => Unsubscribe;

export interface TrackedSnapshotState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: Error | null;
}

const liveCount = { value: 0 };

export function useTrackedSnapshot<T>(
  key: readonly unknown[],
  subscribe: Subscribe<T> | null,
): TrackedSnapshotState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const keyRef = useRef(JSON.stringify(key));
  keyRef.current = JSON.stringify(key);

  useEffect(() => {
    if (!subscribe) return undefined;

    let cancelled = false;
    liveCount.value += 1;
    if (env.mode !== 'production') {
      logger.debug('snapshot:subscribe', { key: keyRef.current, live: liveCount.value });
    }

    let unsub: Unsubscribe | null = null;
    try {
      unsub = subscribe((value) => {
        if (cancelled) return;
        setData(value);
        setLoading(false);
        setError(null);
      });
    } catch (e) {
      if (!cancelled) {
        setError(e instanceof Error ? e : new Error(String(e)));
        setLoading(false);
      }
    }

    return () => {
      cancelled = true;
      if (unsub) unsub();
      liveCount.value -= 1;
      if (env.mode !== 'production') {
        logger.debug('snapshot:unsubscribe', { key: keyRef.current, live: liveCount.value });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyRef.current, subscribe]);

  return { data, loading, error };
}

export function _liveSnapshotCount(): number {
  return liveCount.value;
}
